import React, { useEffect, useState } from 'react'
import { Link, useSearchParams, useLocation } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import './Browse.css'

const CATEGORIES = ['All', 'Writing & Fiction', 'Poetry & Lyrics', 'Journalism & Articles', 'Digital Art & Illustration', 'Traditional / Physical Art', 'Photography', 'Graphic Design', 'Music Composition', 'Music Production & Beats', 'Voice Over & Narration', 'Podcast & Audio', 'Video Editing', 'Animation & Motion', 'Film & Cinematography', 'Game Design', 'Code & Software', 'Architecture & 3D Design', 'Craft & Handmade', 'Sculpture & Ceramics', 'Other']

export default function Browse() {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [forSaleOnly, setForSaleOnly] = useState(false)

  // Read category from URL param
  const categoryParam = searchParams.get('category') || 'All'
  const category = CATEGORIES.includes(categoryParam) ? categoryParam : 'All'

  useEffect(() => { fetchProjects() }, [category, location.key, forSaleOnly]) // eslint-disable-line

  async function fetchProjects() {
    setLoading(true)
    let q = supabase
      .from('projects')
      .select('*, profiles!projects_user_id_fkey(full_name, display_name, username, plan, avatar_url)', { count: 'exact' })
      .eq('visibility', 'public')
      .eq('admin_hidden', false)
      .eq('status', 'verified')
      .order('verified_at', { ascending: false })
      .limit(60)
    if (forSaleOnly) q = q.eq('for_sale', true)
    if (category !== 'All') {
      if (category === 'Other') q = q.in('category', ['Other', 'Other (describe below)'])
      else q = q.eq('category', category)
    }
    const { data, count } = await q
    setProjects(data || [])
    setTotal(count || 0)
    setLoading(false)
  }

  function setCategory(cat) {
    if (cat === 'All') setSearchParams({})
    else setSearchParams({ category: cat })
  }

  const filtered = projects.filter(p =>
    search === '' ||
    p.title.toLowerCase().includes(search.toLowerCase()) ||
    (p.profiles?.display_name || p.profiles?.full_name || '').toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="browse-page page-fade">
      <div className="container">
        <div className="browse-header">
          <div>
            <h1>Browse verified work</h1>
            <p>Human-made creations, confirmed by our review team. Every piece here has been verified — no algorithm, no automation.</p>
          </div>
          {total > 0 && (
            <div className="browse-count-badge">
              <span className="browse-count-dot" />
              {total} verified work{total !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search by title or creator..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="browse-search"
            style={{ flex: 1, minWidth: 200, marginBottom: 0 }}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={forSaleOnly} onChange={e => setForSaleOnly(e.target.checked)} />
            Available for sale
          </label>
        </div>

        <div className="category-filter">
          {CATEGORIES.map(c => (
            <button key={c} className={`cat-btn ${category === c ? 'active' : ''}`} onClick={() => setCategory(c)}>{c}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 80 }}><span className="loading-spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="browse-empty">
            <div style={{ fontSize: 40, marginBottom: 16 }}>🎨</div>
            <h3>No verified works here yet</h3>
            <p>Be the first to get verified in this category.</p>
            <Link to="/login" className="btn btn-primary" style={{ marginTop: 20 }}>Start a project →</Link>
          </div>
        ) : (
          <div className="browse-grid">
            {filtered.map(p => <BrowseCard key={p.id} project={p} />)}
          </div>
        )}
      </div>
    </div>
  )
}

function BrowseCard({ project }) {
  const creator = project.profiles
  const creatorName = creator?.display_name || creator?.full_name || 'Anonymous'
  return (
    <div className="browse-card">
      <Link to={`/project/${project.id}`} className="browse-card-inner">
        <div className="browse-card-banner">
          {project.banner_url
            ? <img src={project.banner_url} alt={project.title} className="browse-card-banner-img" />
            : <div className="browse-card-banner-placeholder"><span>{project.title.charAt(0)}</span></div>
          }
          <div className="browse-card-banner-badges">
            <span className="browse-card-cat">{project.custom_category ? `Other / ${project.custom_category}` : project.category}</span>
            <span className="browse-verified-dot" title="Verified">✓</span>
          </div>
          {project.for_sale && (
            <span className="browse-card-price-badge">${project.price}</span>
          )}
        </div>
        <div className="browse-card-body">
          <h3 className="browse-card-title">{project.title}</h3>
          {project.description && (
            <p className="browse-card-desc">{project.description.slice(0, 100)}{project.description.length > 100 ? '…' : ''}</p>
          )}
        </div>
      </Link>
      <div className="browse-card-footer">
        {creator?.username
          ? <Link to={`/u/${creator.username}`} className="browse-card-creator">
              {creator.avatar_url
                ? <img src={creator.avatar_url} alt={creatorName} className="browse-card-avatar" />
                : <span className="browse-card-avatar browse-card-avatar-initials">{creatorName.charAt(0).toUpperCase()}</span>
              }
              {creatorName}
            </Link>
          : <span className="browse-card-creator">
              <span className="browse-card-avatar browse-card-avatar-initials">{creatorName.charAt(0).toUpperCase()}</span>
              {creatorName}
            </span>
        }
        <span className="browse-card-date">{new Date(project.verified_at || project.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
      </div>
    </div>
  )
}
